import type { SpectralInput } from './spectral.js';

const FFT_SIZE = 4096;
const HOP_SIZE = 2048;
const WINDOW_SECONDS = 4;
const MEDIAN_WINDOW = 50; // bins on each side -> 101-bin local window
const PROMINENCE_DB = 6;
const TOP_N = 5;

/**
 * Detect prominent narrow-band resonance peaks in the averaged magnitude
 * spectrum of (up to) the first `WINDOW_SECONDS` seconds of audio.
 *
 * Algorithm:
 *   1. Take the first 4 seconds (or full signal if shorter).
 *   2. STFT with a 4096-point Hann window, 50% hop, average magnitude.
 *   3. Convert to dB (20·log10|X|).
 *   4. For each interior bin: it's a peak if mag[k] > mag[k-1] and mag[k] > mag[k+1]
 *      and (mag[k] - localMedian) >= PROMINENCE_DB, where localMedian is over
 *      a 101-bin window around k.
 *   5. Return the top-5 peaks by prominence (mag[k] - localMedian), DESC,
 *      as frequencies in Hz.
 */
export function detectResonancePeaks(input: SpectralInput): number[] {
  const { samples, sample_rate } = input;
  if (samples.length < FFT_SIZE || sample_rate <= 0) return [];

  const windowSamples = Math.min(samples.length, Math.floor(WINDOW_SECONDS * sample_rate));
  const slice = samples.subarray(0, windowSamples);
  const halfBins = FFT_SIZE / 2;

  const window = hannWindow(FFT_SIZE);
  const re = new Float64Array(FFT_SIZE);
  const im = new Float64Array(FFT_SIZE);
  const magSumSq = new Float64Array(halfBins);
  let frameCount = 0;

  for (let start = 0; start + FFT_SIZE <= slice.length; start += HOP_SIZE) {
    for (let i = 0; i < FFT_SIZE; i++) {
      re[i] = slice[start + i] * window[i];
      im[i] = 0;
    }
    fftRadix2(re, im);
    for (let k = 0; k < halfBins; k++) {
      const m = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
      magSumSq[k] += m * m;
    }
    frameCount++;
  }
  if (frameCount === 0) return [];

  const magDb = new Float64Array(halfBins);
  for (let k = 0; k < halfBins; k++) {
    const p = magSumSq[k] / frameCount;
    magDb[k] = p > 1e-30 ? 10 * Math.log10(p) : -300;
  }

  const binHz = sample_rate / FFT_SIZE;

  type Cand = { freq: number; prominence: number };
  const cands: Cand[] = [];
  for (let k = 1; k < halfBins - 1; k++) {
    if (magDb[k] <= magDb[k - 1] || magDb[k] <= magDb[k + 1]) continue;
    const lo = Math.max(0, k - MEDIAN_WINDOW);
    const hi = Math.min(halfBins - 1, k + MEDIAN_WINDOW);
    const localMedian = median(magDb, lo, hi);
    const prominence = magDb[k] - localMedian;
    if (prominence >= PROMINENCE_DB) {
      cands.push({ freq: k * binHz, prominence });
    }
  }

  cands.sort((a, b) => b.prominence - a.prominence);
  return cands.slice(0, TOP_N).map((c) => c.freq);
}

function median(arr: Float64Array, lo: number, hi: number): number {
  const n = hi - lo + 1;
  const tmp = new Float64Array(n);
  for (let i = 0; i < n; i++) tmp[i] = arr[lo + i];
  tmp.sort();
  const mid = n >> 1;
  return n % 2 === 1 ? tmp[mid] : 0.5 * (tmp[mid - 1] + tmp[mid]);
}

function hannWindow(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return w;
}

// In-place Cooley-Tukey radix-2 FFT. n must be a power of 2.
function fftRadix2(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const theta = (-2 * Math.PI) / size;
    const wpr = Math.cos(theta);
    const wpi = Math.sin(theta);
    for (let start = 0; start < n; start += size) {
      let wr = 1;
      let wi = 0;
      for (let k = 0; k < half; k++) {
        const i0 = start + k;
        const i1 = i0 + half;
        const tr = wr * re[i1] - wi * im[i1];
        const ti = wr * im[i1] + wi * re[i1];
        re[i1] = re[i0] - tr;
        im[i1] = im[i0] - ti;
        re[i0] = re[i0] + tr;
        im[i0] = im[i0] + ti;
        const nwr = wr * wpr - wi * wpi;
        wi = wr * wpi + wi * wpr;
        wr = nwr;
      }
    }
  }
}
